import React, { useEffect, useState, useCallback } from "react";
import Layout from "../layout/Layout";
import LoadingOverlay from "react-loading-overlay-ts";
import Api from "../Config/ApiConfig";
import Pagination from "../Component/Pagination";
import { toast } from "react-toastify";
import moment from "moment";

const STATUS_COLORS = {
  OPEN:               { bg: "bg-yellow-100",  text: "text-yellow-800"  },
  IN_PROGRESS:        { bg: "bg-blue-100",    text: "text-blue-800"    },
  AWAITING_FOLLOWUP:  { bg: "bg-purple-100",  text: "text-purple-800"  },
  CLOSED:             { bg: "bg-gray-100",    text: "text-gray-600"    },
};

const TYPE_COLORS = {
  GENERAL_INQUIRY: { bg: "bg-emerald-100", text: "text-emerald-800" },
  BOOKING_SUPPORT: { bg: "bg-blue-100",    text: "text-blue-800"    },
  CARER_INQUIRY:   { bg: "bg-indigo-100",  text: "text-indigo-800"  },
  COMPLAINT:       { bg: "bg-red-100",     text: "text-red-800"     },
  PARTNERSHIP:     { bg: "bg-orange-100",  text: "text-orange-800"  },
  OTHER:           { bg: "bg-gray-100",    text: "text-gray-700"    },
};

const REQUEST_TYPE_LABELS = {
  GENERAL_INQUIRY: "General Inquiry",
  BOOKING_SUPPORT: "Booking Support",
  CARER_INQUIRY:   "Carer Inquiry",
  COMPLAINT:       "Complaint",
  PARTNERSHIP:     "Partnership",
  OTHER:           "Other",
};

const Badge = ({ value, map }) => {
  const c = map[value] || { bg: "bg-gray-100", text: "text-gray-700" };
  const label = REQUEST_TYPE_LABELS[value] || value?.replace(/_/g, " ");
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${c.bg} ${c.text}`}>
      {label}
    </span>
  );
};

const STATUSES = ["OPEN", "IN_PROGRESS", "AWAITING_FOLLOWUP", "CLOSED"];
const REQUEST_TYPES = Object.keys(REQUEST_TYPE_LABELS);

export default function ContactForms() {
  const [loading, setLoading]           = useState(true);
  const [data, setData]                 = useState([]);
  const [total, setTotal]               = useState(0);
  const [page, setPage]                 = useState(1);
  const [limit]                         = useState(10);
  const [search, setSearch]             = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [typeFilter, setTypeFilter]     = useState("");
  const [startDate, setStartDate]       = useState("");
  const [endDate, setEndDate]           = useState("");
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const [selected, setSelected]         = useState(null);

  // Detail panel state
  const [newStatus, setNewStatus]       = useState("");
  const [statusNote, setStatusNote]     = useState("");
  const [commentText, setCommentText]   = useState("");
  const [followupDate, setFollowupDate] = useState("");
  const [followupNote, setFollowupNote] = useState("");
  const [saving, setSaving]             = useState(false);

  const fetchData = useCallback(async (overrides = {}) => {
    try {
      setLoading(true);
      const p  = overrides.page         ?? page;
      const s  = overrides.search       ?? search;
      const st = overrides.statusFilter ?? statusFilter;
      const tt = overrides.typeFilter   ?? typeFilter;
      const sd = overrides.startDate    ?? startDate;
      const ed = overrides.endDate      ?? endDate;

      const params = new URLSearchParams({ page: p, limit });
      if (s)  params.set("search", s);
      if (st) params.set("status", st);
      if (tt) params.set("request_type", tt);
      if (sd) params.set("startDate", sd);
      if (ed) params.set("endDate", ed);

      const res = await Api.get(`/contact-form?${params.toString()}`);
      if (res.data?.success) {
        setData(res.data.data || []);
        setTotal(res.data.totalItems || 0);
      }
    } catch {
      toast.error("Failed to load contact forms.");
    } finally {
      setLoading(false);
    }
  }, [page, search, statusFilter, typeFilter, startDate, endDate, limit]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handlePageClick = (e) => {
    const newPage = e.selected + 1;
    setPage(newPage);
    fetchData({ page: newPage });
  };

  const handleClear = () => {
    setSearch(""); setStatusFilter(""); setTypeFilter("");
    setStartDate(""); setEndDate(""); setPage(1);
    fetchData({ page: 1, search: "", statusFilter: "", typeFilter: "", startDate: "", endDate: "" });
  };

  const openDetail = (row) => {
    setSelected(row);
    setNewStatus(row.status);
    setStatusNote(""); setCommentText(""); setFollowupDate(""); setFollowupNote("");
  };

  const refreshSelected = async (id) => {
    const res = await Api.get(`/contact-form/${id}`);
    if (res.data?.success) {
      setSelected(res.data.data);
      setNewStatus(res.data.data.status);
    }
    fetchData();
  };

  const handleStatusUpdate = async () => {
    if (!newStatus) return;
    try {
      setSaving(true);
      await Api.patch(`/contact-form/${selected._id}/status`, { status: newStatus, note: statusNote });
      toast.success("Status updated");
      setStatusNote("");
      await refreshSelected(selected._id);
    } catch { toast.error("Failed to update status"); }
    finally { setSaving(false); }
  };

  const handleAddComment = async () => {
    if (!commentText.trim()) return;
    try {
      setSaving(true);
      await Api.post(`/contact-form/${selected._id}/comment`, { text: commentText });
      toast.success("Comment added");
      setCommentText("");
      await refreshSelected(selected._id);
    } catch { toast.error("Failed to add comment"); }
    finally { setSaving(false); }
  };

  const handleAddFollowup = async () => {
    if (!followupDate) return;
    try {
      setSaving(true);
      await Api.post(`/contact-form/${selected._id}/followup`, { date: followupDate, note: followupNote });
      toast.success("Follow-up scheduled");
      setFollowupDate(""); setFollowupNote("");
      await refreshSelected(selected._id);
    } catch { toast.error("Failed to schedule follow-up"); }
    finally { setSaving(false); }
  };

  const pageCount = Math.ceil(total / limit) || 1;
  const hasFilters = search || statusFilter || typeFilter || startDate || endDate;

  return (
    <Layout>
      <LoadingOverlay active={loading} text="Loading...">
        <div className="dashboard_data_wrap">
          <div className="dashbord_cont_wrapper">

            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-2xl font-bold text-gray-900 m-0">Contact Forms</h2>
                <p className="text-sm text-gray-500 mt-1">Enquiries and support requests submitted via the website</p>
              </div>
              <div className="text-sm text-gray-500 bg-gray-100 px-3 py-1.5 rounded-lg font-medium">
                {total} total
              </div>
            </div>

            {/* Filters */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm mb-6">
              <div
                className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50 transition-colors"
                onClick={() => setFiltersExpanded(!filtersExpanded)}
              >
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center">
                    <i className="fas fa-filter text-emerald-600 text-sm"></i>
                  </div>
                  <div>
                    <h3 className="text-base font-semibold text-gray-900 m-0">Filters</h3>
                    <p className="text-xs mt-0.5 m-0">
                      {!filtersExpanded && hasFilters
                        ? <span className="text-emerald-600">Filters active</span>
                        : <span className="text-gray-400">Click to expand</span>}
                    </p>
                  </div>
                </div>
                <svg className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${filtersExpanded ? "rotate-180" : ""}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>

              {filtersExpanded && (
                <div className="px-4 pb-4 border-t border-gray-100">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
                    <div className="lg:col-span-3">
                      <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">Search</label>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          placeholder="Reference ID, name, email, phone..."
                          value={search}
                          onChange={e => setSearch(e.target.value)}
                          onKeyDown={e => e.key === "Enter" && fetchData({ page: 1 })}
                          className="flex-1 px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                        />
                        <button onClick={() => { setPage(1); fetchData({ page: 1 }); }} className="common-btn px-4 py-2 text-sm rounded-lg">Search</button>
                        {hasFilters && (
                          <button onClick={handleClear} className="px-4 py-2 text-sm border border-gray-300 rounded-lg text-gray-600 hover:bg-gray-50 transition-colors">Clear</button>
                        )}
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">Status</label>
                      <select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1); fetchData({ page: 1, statusFilter: e.target.value }); }}
                        className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 bg-white">
                        <option value="">All statuses</option>
                        {STATUSES.map(s => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">Request Type</label>
                      <select value={typeFilter} onChange={e => { setTypeFilter(e.target.value); setPage(1); fetchData({ page: 1, typeFilter: e.target.value }); }}
                        className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 bg-white">
                        <option value="">All types</option>
                        {REQUEST_TYPES.map(t => <option key={t} value={t}>{REQUEST_TYPE_LABELS[t]}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">Date From</label>
                      <input type="date" value={startDate} onChange={e => { setStartDate(e.target.value); setPage(1); fetchData({ page: 1, startDate: e.target.value }); }}
                        className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500" />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">Date To</label>
                      <input type="date" value={endDate} onChange={e => { setEndDate(e.target.value); setPage(1); fetchData({ page: 1, endDate: e.target.value }); }}
                        className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500" />
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Table */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
              <div className="user_list_wrap table-responsive">
                <table className="table table-striped" style={{ fontSize: 13, color: "#111827" }}>
                  <thead>
                    <tr>
                      <th>Reference</th>
                      <th>Name</th>
                      <th>Email / Phone</th>
                      <th>Type</th>
                      <th>Status</th>
                      <th>Follow-ups</th>
                      <th>Submitted</th>
                      <th>View</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.map((row) => (
                      <tr key={row._id}>
                        <td className="font-mono text-xs font-semibold text-gray-700">{row.referenceId}</td>
                        <td className="font-medium">{row.first_name} {row.last_name}</td>
                        <td>
                          <div className="text-xs text-gray-700">{row.email}</div>
                          {row.phone && <div className="text-xs text-gray-400">{row.phone}</div>}
                        </td>
                        <td><Badge value={row.request_type} map={TYPE_COLORS} /></td>
                        <td><Badge value={row.status} map={STATUS_COLORS} /></td>
                        <td className="text-xs text-gray-500 text-center">
                          {row.followups?.length > 0
                            ? <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-purple-50 text-purple-700 font-semibold">{row.followups.length}</span>
                            : <span className="text-gray-300">—</span>}
                        </td>
                        <td className="whitespace-nowrap text-xs text-gray-500">
                          <div>{moment(row.createdAt).format("DD MMM YYYY")}</div>
                          <div className="text-gray-400">{moment(row.createdAt).format("HH:mm")}</div>
                        </td>
                        <td>
                          <button onClick={() => openDetail(row)} className="text-emerald-600 hover:text-emerald-800 text-xs font-semibold underline">
                            View
                          </button>
                        </td>
                      </tr>
                    ))}
                    {!loading && data.length === 0 && (
                      <tr>
                        <td colSpan={8} className="text-center py-10 text-gray-400">
                          <i className="fas fa-inbox text-3xl mb-2 block"></i>
                          No contact forms found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <Pagination handlePageClick={handlePageClick} pageCount={pageCount} />
          </div>
        </div>
      </LoadingOverlay>

      {/* Detail Modal */}
      {selected && (
        <div className="fixed inset-0 z-50 flex items-start justify-center p-4 overflow-y-auto"
          style={{ background: "rgba(0,0,0,0.5)" }}
          onClick={() => setSelected(null)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl my-8"
            onClick={e => e.stopPropagation()}>

            {/* Modal Header */}
            <div className="flex items-center justify-between p-5 border-b border-gray-100">
              <div>
                <h3 className="text-lg font-bold text-gray-900 m-0">{selected.referenceId}</h3>
                <p className="text-xs text-gray-400 mt-0.5 m-0">Submitted {moment(selected.createdAt).format("DD MMM YYYY, HH:mm")}</p>
              </div>
              <button onClick={() => setSelected(null)} className="text-gray-400 hover:text-gray-600 text-2xl font-bold leading-none">×</button>
            </div>

            <div className="p-5 space-y-6">

              {/* Contact Info */}
              <section>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Contact</p>
                <div className="grid grid-cols-2 gap-3">
                  <Detail label="Name">{selected.first_name} {selected.last_name}</Detail>
                  <Detail label="Email">{selected.email}</Detail>
                  {selected.phone && <Detail label="Phone">{selected.phone}</Detail>}
                  <Detail label="Type"><Badge value={selected.request_type} map={TYPE_COLORS} /></Detail>
                </div>
              </section>

              {/* Message */}
              <section className="border-t border-gray-100 pt-4">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Message</p>
                <p className="text-sm text-gray-700 leading-relaxed bg-gray-50 rounded-lg p-3">{selected.message}</p>
              </section>

              {/* Status Update */}
              <section className="border-t border-gray-100 pt-4">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Update Status</p>
                <div className="flex gap-2 mb-2">
                  <select value={newStatus} onChange={e => setNewStatus(e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 bg-white">
                    {STATUSES.map(s => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
                  </select>
                  <button onClick={handleStatusUpdate} disabled={saving || newStatus === selected.status}
                    className="common-btn px-4 py-2 text-sm rounded-lg disabled:opacity-50">
                    Save
                  </button>
                </div>
                <input type="text" placeholder="Optional note..." value={statusNote} onChange={e => setStatusNote(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500" />
              </section>

              {/* Status History */}
              {selected.statusHistory?.length > 0 && (
                <section className="border-t border-gray-100 pt-4">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Status History</p>
                  <div className="space-y-2">
                    {[...selected.statusHistory].reverse().map((h, i) => (
                      <div key={i} className="flex items-start gap-3 text-xs">
                        <div className="mt-0.5 w-2 h-2 rounded-full bg-emerald-400 flex-shrink-0"></div>
                        <div>
                          <span className="font-semibold text-gray-800">{h.status.replace(/_/g, " ")}</span>
                          {h.note && <span className="text-gray-500 ml-2">— {h.note}</span>}
                          <div className="text-gray-400">{moment(h.changedAt).format("DD MMM YYYY, HH:mm")}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </section>
              )}

              {/* Add Comment */}
              <section className="border-t border-gray-100 pt-4">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Internal Comments</p>
                {selected.comments?.length > 0 && (
                  <div className="space-y-2 mb-3">
                    {selected.comments.map((c, i) => (
                      <div key={i} className="bg-yellow-50 border border-yellow-100 rounded-lg p-3 text-sm">
                        <p className="text-gray-800 m-0">{c.text}</p>
                        <p className="text-xs text-gray-400 mt-1 m-0">{moment(c.createdAt).format("DD MMM YYYY, HH:mm")}</p>
                      </div>
                    ))}
                  </div>
                )}
                <div className="flex gap-2">
                  <input type="text" placeholder="Add a comment..." value={commentText} onChange={e => setCommentText(e.target.value)}
                    onKeyDown={e => e.key === "Enter" && handleAddComment()}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500" />
                  <button onClick={handleAddComment} disabled={saving || !commentText.trim()}
                    className="common-btn px-4 py-2 text-sm rounded-lg disabled:opacity-50">
                    Add
                  </button>
                </div>
              </section>

              {/* Follow-ups */}
              <section className="border-t border-gray-100 pt-4">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Follow-ups</p>
                {selected.followups?.length > 0 && (
                  <div className="space-y-2 mb-3">
                    {selected.followups.map((f, i) => (
                      <div key={i} className="flex items-start gap-3 bg-purple-50 border border-purple-100 rounded-lg p-3 text-sm">
                        <i className="fas fa-calendar-alt text-purple-400 mt-0.5"></i>
                        <div>
                          <span className="font-semibold text-gray-800">{moment(f.date).format("DD MMM YYYY")}</span>
                          {f.note && <span className="text-gray-500 ml-2">— {f.note}</span>}
                          <div className="text-xs text-gray-400">{moment(f.createdAt).format("DD MMM YYYY, HH:mm")}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                <div className="grid grid-cols-2 gap-2">
                  <input type="date" value={followupDate} onChange={e => setFollowupDate(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500" />
                  <input type="text" placeholder="Note (optional)" value={followupNote} onChange={e => setFollowupNote(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500" />
                </div>
                <button onClick={handleAddFollowup} disabled={saving || !followupDate}
                  className="mt-2 common-btn px-4 py-2 text-sm rounded-lg disabled:opacity-50 w-full">
                  Schedule Follow-up
                </button>
              </section>

            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}

function Detail({ label, children }) {
  return (
    <div>
      <p className="text-xs text-gray-400 font-medium mb-0.5">{label}</p>
      <div className="text-sm font-semibold text-gray-800">{children}</div>
    </div>
  );
}
